import { useMutation, useQuery } from '@apollo/client';
import { FileType } from '@procureai/constellation/foundation/file';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { type FileType as StoredFileVariant, useFileStorage } from '@procureai/uix';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBatchedFileUpload } from '~/hooks/useMultiFileUploadProgress/useMultiFileUploadProgress';
import { useChatSession } from '~/modules/Module.hooks';

import {
  INTAKE_REQUIREMENTS_SKIP_MUTATION,
  INTAKE_REQUIREMENTS_UPLOAD_MUTATION,
} from './IntakeRequirementsUploadStep.mutation.gql';
import { INTAKE_REQUIREMENTS_QUERY } from './IntakeRequirementsUploadStep.query.gql';

type RequirementsUploadRun = {
  id?: string;
  fileId: string;
  status?: 'SUCCESS' | 'ERROR' | 'SKIPPED';
};

export const useRequirementsUploadStep = () => {
  const session = useChatSession();
  const { onError } = useCustomSnackbar();
  const [errors, setErrors] = useState<string[]>([]);
  const { upload: falconUpload } = useFileStorage();

  // Query for existing requirements extraction data
  const { data: queriedData, loading: queryLoading } = useQuery(INTAKE_REQUIREMENTS_QUERY, {
    variables: { id: session.id },
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [analyzeRequirements, { loading: isAnalyzing }] = useMutation(INTAKE_REQUIREMENTS_UPLOAD_MUTATION, {
    onError,
    refetchQueries: [{ query: INTAKE_REQUIREMENTS_QUERY, variables: { id: session.id } }],
  });

  const [skipRequirements, { loading: skipLoading }] = useMutation(INTAKE_REQUIREMENTS_SKIP_MUTATION, {
    onError,
    variables: {
      data: { chatSessionId: session.id },
    },
    refetchQueries: [{ query: INTAKE_REQUIREMENTS_QUERY, variables: { id: session.id } }],
  });

  // File upload function
  const upload = useCallback(
    async (file: File): Promise<RequirementsUploadRun> => {
      // Validate file type
      const allowedTypes = [
        'text/plain',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Unsupported file type. Please upload a TXT, PDF, or DOCX file.');
      }

      // TODO: Add 10-page limit validation for PDF/DOCX files
      // This would require parsing the file to count pages, which is complex for frontend
      // For now, we can rely on backend validation or file size limits

      const { error, data } = await falconUpload({
        // TODO: Use FileType.REQUIREMENTS_DOCUMENT when available. The cast: uix types variants as
        // ATTACHMENT | PROFILE_PICTURE | OTHER, but its Falcon adapter forwards QUOTE unchanged.
        data: { file, variant: FileType.QUOTE as unknown as StoredFileVariant },
      });

      if (error) {
        throw new Error('Failed to upload requirements document');
      }

      return {
        fileId: data.uploadId,
        status: 'SUCCESS', // Initial success status after upload
      };
    },
    [falconUpload],
  );

  const { uploadBatch, isLoading: isUploading, files } = useBatchedFileUpload<RequirementsUploadRun>(upload);

  useEffect(() => {
    async function analyze() {
      const uploadedFiles = files.filter((file) => file.status === 'SUCCESS');
      if (uploadedFiles.length > 0) {
        await analyzeRequirements({
          variables: {
            data: {
              fileIds: uploadedFiles.map((file) => file.fileId),
              chatSessionId: session.id,
            },
          },
        });
      }
    }

    analyze().catch(onError);
  }, [files, analyzeRequirements, session.id, onError]);

  // Extract requirements data from the query
  const requirementsData = useMemo(() => {
    return queriedData?.sevenbridges?.chatSession?.requirementsExtraction;
  }, [queriedData]);

  const fileIds = requirementsData?.fileIds;
  const hasRunningAnalysis =
    requirementsData && !['SUCCESS', 'ERROR', 'SKIPPED'].includes(requirementsData.processingStatus);
  const isFailedAnalysis = requirementsData?.processingStatus === 'ERROR';

  // Handle skip
  const handleSkip = useCallback(async () => {
    try {
      await skipRequirements();
    } catch (error) {
      onError('Skip requirements failed');
      console.error('Skip requirements failed:', error);
    }
  }, [skipRequirements, onError]);

  return {
    // Data
    requirementsData,
    fileIds,
    errors,

    // State
    loading: queryLoading,
    isUploading: isUploading || isAnalyzing,
    hasRunningAnalysis,
    isFailedAnalysis,
    skipLoading,

    // Actions
    uploadBatch,
    handleSkip,
    setErrors,
  };
};
